import React, { useEffect, useRef } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { Box, InputAdornment, TextField, TextFieldProps } from 'edenred-ui';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';

import { getIsUserImpersonate } from '@epi-selectors/login';
import { BenefitType, FundsLoadingTypes } from '@epi-constants/benefitTypes';
import { createRules } from '@epi-forms/helpers';
import { getSettings } from '@epi-selectors/settings';
import { toComma } from '@epi-helpers/commaHelper';
import {
  numberBetween,
  required,
  number,
  virikeMaxAmount,
  virikeMinAmount,
  lounariAmount,
  dailyLimitDecrese
} from '@epi-helpers/formHelpers/validators';
import { IDailyLimitField } from '@epi-models/pages/BenefitGroups';

export const DailyLimitField = ({
  readOnly = false,
  label = '',
  benefitType,
  helperText = '',
  disabled = false,
  options = [],
  name,
  fundsLoadingTypeName,
  maxAmounts,
  currentMaxAmount,
  ...rest
}: IDailyLimitField & TextFieldProps) => {
  const theme = useTheme();
  const { control, watch, trigger } = useFormContext();
  const settings = useSelector(getSettings);
  const isUserImpersonate = useSelector(getIsUserImpersonate);

  const fundsLoadingType = watch(fundsLoadingTypeName);
  const isLounari = benefitType === BenefitType.Lunch;
  const isVirike = benefitType === BenefitType.Recreational;
  const decreaseNotAllowed = currentMaxAmount && !isUserImpersonate;
  const validators = [
    required,
    number,
    numberBetween(maxAmounts.at(-1)!, maxAmounts.at(0)!)
  ];
  const lounariValidators = [
    required,
    lounariAmount(settings.lunchMinAmount, settings.lunchMaxAmount),
    ...(decreaseNotAllowed ? [dailyLimitDecrese(currentMaxAmount)] : [])
  ];
  const virikeValidators = [
    required,
    virikeMinAmount,
    virikeMaxAmount(
      fundsLoadingType === FundsLoadingTypes.TopUpRequest
        ? settings.recreationalMaxAmountForTopUpRequest
        : settings.recreationalMaxAmount
    )
  ];
  const getRules = () => {
    if (isVirike) return createRules(virikeValidators);
    if (isLounari) return createRules(lounariValidators);
    return createRules(validators);
  };
  const rules = getRules();

  const prevFundsLoadingTypeRef = useRef(fundsLoadingType);

  useEffect(() => {
    const prev = prevFundsLoadingTypeRef.current;
    if (isVirike && prev !== fundsLoadingType) {
      trigger(name);
    }
    prevFundsLoadingTypeRef.current = fundsLoadingType;
  }, [fundsLoadingType, isVirike, isLounari, name, trigger]);

  const {
    field: { ref, value, onChange: setValue },
    fieldState: { error }
  } = useController({
    name,
    control,
    rules
  });

  const onChange = event => {
    const formattedValue = toComma(event.target.value || '');
    const decimalRegex = /^\d*,?\d{0,2}$/;

    if (formattedValue === '' || decimalRegex.test(formattedValue)) {
      setValue(formattedValue);
    }
  };
  return (
    <TextField
      label={label}
      value={value}
      disabled={disabled}
      readOnly={readOnly}
      id={`daily-limit-select-${benefitType}`}
      error={!!error}
      helperText={error?.message ? error?.message : helperText}
      inputRef={ref}
      onChange={onChange}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <Box fontSize={theme.sizeMedium}>&euro;</Box>
          </InputAdornment>
        )
      }}
      SelectProps={{
        MenuProps: {
          BackdropProps: {
            sx: {
              backgroundColor: 'transparent'
            }
          }
        }
      }}
      {...rest}
    >
      {options
        .filter(option => option.upToDate)
        .map(option => (
          <TextField.Item key={option.id} value={option.id}>
            {option.display}
          </TextField.Item>
        ))}
    </TextField>
  );
};
